import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, PrimaryButton } from "@/components/DataViews";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Plus, Trash2, Save, Send } from "lucide-react";

export default function IndentNew() {
  const nav = useNavigate();
  const { fieldHidden } = useAuth();
  const pricingHidden = fieldHidden("indent", "items.estimated_cost");
  const [masters, setMasters] = useState({ projects: [], sites: [], materials: [], units: [], departments: [] });
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    project_id: "", project_name: "",
    site_id: "", site_name: "",
    department: "",
    required_by_date: "",
    reason: "", purpose: "", priority: "Routine", remarks: "",
    items: [{ material_name: "", description: "", unit: "", required_qty: 1, current_stock: 0, estimated_cost: 0 }],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all(["projects", "sites", "materials", "units", "departments"].map((t) =>
      api.get(`/masters/${t}`).then((r) => [t, r.data])
    )).then((results) => {
      const m = {}; results.forEach(([k, v]) => m[k] = v); setMasters(m);
    });
  }, []);

  const setItem = (idx, patch) => {
    setForm((f) => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
  };
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { material_name: "", description: "", unit: "", required_qty: 1, current_stock: 0, estimated_cost: 0 }] }));
  const rmItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const save = async (submit = false) => {
    setSaving(true);
    try {
      const { data } = await api.post("/indents", form);
      if (submit) {
        await api.post(`/indents/${data.id}/submit`);
      }
      toast.success(submit ? "Indent submitted to QS" : "Indent saved as draft");
      nav("/indents");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="indent-new-page">
      <PageHeader title="New Material Indent" subtitle="Raise a request for materials required at site." />

      <Card className="p-6 border-border">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wide font-semibold">Date</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" data-testid="indent-date" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide font-semibold">Project *</Label>
            <Select value={form.project_id} onValueChange={(v) => {
              const p = masters.projects.find((x) => x.id === v);
              setForm({ ...form, project_id: v, project_name: p?.name || "" });
            }}>
              <SelectTrigger className="mt-1.5" data-testid="indent-project"><SelectValue placeholder="Select project" /></SelectTrigger>
              <SelectContent>{masters.projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide font-semibold">Site *</Label>
            <Select value={form.site_id} onValueChange={(v) => {
              const s = masters.sites.find((x) => x.id === v);
              setForm({ ...form, site_id: v, site_name: s?.name || "" });
            }}>
              <SelectTrigger className="mt-1.5" data-testid="indent-site"><SelectValue placeholder="Select site" /></SelectTrigger>
              <SelectContent>{masters.sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide font-semibold">Department</Label>
            <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="mt-1.5" data-testid="indent-department" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide font-semibold">Required By</Label>
            <Input type="date" value={form.required_by_date} onChange={(e) => setForm({ ...form, required_by_date: e.target.value })} className="mt-1.5" data-testid="indent-required-by" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide font-semibold">Priority</Label>
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger className="mt-1.5" data-testid="indent-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Routine">Routine</SelectItem>
                <SelectItem value="Urgent">Urgent</SelectItem>
                <SelectItem value="Emergency">Emergency</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wide font-semibold">Purpose</Label>
            <Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} className="mt-1.5" placeholder="E.g. Column casting - Block A" data-testid="indent-purpose" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide font-semibold">Reason</Label>
            <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1.5" data-testid="indent-reason" />
          </div>
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Line Items</div>
            <Button variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn"><Plus size={14} className="mr-1" />Add Item</Button>
          </div>
          <div className="border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-[10px] uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2">Material</th>
                  <th className="text-left px-3 py-2">Unit</th>
                  <th className="text-right px-3 py-2 w-24">Req Qty</th>
                  <th className="text-right px-3 py-2 w-24">Stock</th>
                  {!pricingHidden && <th className="text-right px-3 py-2 w-28">Est. Cost</th>}
                  <th className="text-left px-3 py-2">Description</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((it, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-2 py-1">
                      <Select value={it.material_name} onValueChange={(v) => {
                        const m = masters.materials.find((x) => x.name === v);
                        setItem(i, { material_name: v, unit: m?.extra?.unit || it.unit });
                      }}>
                        <SelectTrigger className="h-9" data-testid={`item-material-${i}`}><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{masters.materials.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-2 py-1">
                      <Input value={it.unit} onChange={(e) => setItem(i, { unit: e.target.value })} className="h-9 w-24" data-testid={`item-unit-${i}`} />
                    </td>
                    <td className="px-2 py-1"><Input type="number" value={it.required_qty} onChange={(e) => setItem(i, { required_qty: parseFloat(e.target.value) || 0 })} className="h-9 text-right" data-testid={`item-qty-${i}`} /></td>
                    <td className="px-2 py-1"><Input type="number" value={it.current_stock} onChange={(e) => setItem(i, { current_stock: parseFloat(e.target.value) || 0 })} className="h-9 text-right" /></td>
                    {!pricingHidden && <td className="px-2 py-1"><Input type="number" value={it.estimated_cost} onChange={(e) => setItem(i, { estimated_cost: parseFloat(e.target.value) || 0 })} className="h-9 text-right" /></td>}
                    <td className="px-2 py-1"><Input value={it.description} onChange={(e) => setItem(i, { description: e.target.value })} className="h-9" /></td>
                    <td className="px-2 py-1 text-center">
                      <button onClick={() => rmItem(i)} className="text-red-600 hover:text-red-800" data-testid={`item-remove-${i}`}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-4">
          <Label className="text-xs uppercase tracking-wide font-semibold">Remarks</Label>
          <Textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} rows={3} className="mt-1.5" data-testid="indent-remarks" />
        </div>

        <div className="mt-6 flex justify-end gap-2 border-t border-border pt-4">
          <Button variant="outline" onClick={() => nav("/indents")} data-testid="cancel-btn">Cancel</Button>
          <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="save-draft-btn"><Save size={14} className="mr-2" />Save Draft</Button>
          <PrimaryButton onClick={() => save(true)} disabled={saving} data-testid="submit-indent-btn"><Send size={14} className="mr-2" />Save & Submit</PrimaryButton>
        </div>
      </Card>
    </div>
  );
}
